import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";

const itemSx = {
  mx: 1.5,
  my: 0.25,
  px: 1.75,
  py: 1,
  borderRadius: 2,
  color: "surfaces.onDark",
  transition: "background-color .2s ease, transform .2s ease",
  "& .MuiListItemIcon-root": {
    color: "surfaces.onDark",
    opacity: 0.75,
    minWidth: 38,
    transition: "opacity .2s ease, color .2s ease",
  },
  "& .MuiListItemText-primary": {
    fontWeight: 500,
    fontSize: "0.95rem",
  },
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.08)",
    transform: "translateX(2px)",
    "& .MuiListItemIcon-root": {
      opacity: 1,
      color: "accent.main",
    },
  },
};

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

export default function PwaInstallButton({ onClose, variant = "list" }) {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(() => isStandalone());
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  const isIos = useMemo(() => {
    if (typeof window === "undefined") return false;
    return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };

    const handleInstalled = () => {
      setInstalled(true);
      setInstallPrompt(null);
      setInstructionsOpen(false);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  const handleClick = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      await installPrompt.userChoice;
      setInstallPrompt(null);
      onClose?.();
      return;
    }

    setInstructionsOpen(true);
  };

  if (installed) return null;

  return (
    <>
      {variant === "button" ? (
        <Button
          onClick={handleClick}
          startIcon={<DownloadRoundedIcon fontSize="small" />}
          variant="contained"
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Installera appen
        </Button>
      ) : (
        <List sx={{ py: 0 }}>
          <ListItemButton onClick={handleClick} sx={itemSx}>
            <ListItemIcon>
              <DownloadRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Installera appen" />
          </ListItemButton>
        </List>
      )}

      <Dialog
        open={instructionsOpen}
        onClose={() => setInstructionsOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Lägg till på hemskärmen</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Om ingen installationsruta visas kan du lägga till genvägen manuellt
            från webbläsarens meny.
          </Typography>

          {isIos ? (
            <Box sx={{ display: "grid", gap: 1.25 }}>
              <Typography>
                1. Tryck på dela-knappen <IosShareRoundedIcon fontSize="small" />.
              </Typography>
              <Typography>2. Välj “Lägg till på hemskärmen”.</Typography>
              <Typography>3. Tryck på “Lägg till”.</Typography>
            </Box>
          ) : (
            <Box sx={{ display: "grid", gap: 1.25 }}>
              <Typography>
                1. Öppna sidan i Chrome.
              </Typography>
              <Typography>
                2. Tryck på menyn <MoreVertRoundedIcon fontSize="small" />.
              </Typography>
              <Typography>
                3. Välj “Installera app” eller “Lägg till på startskärmen”.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInstructionsOpen(false)}>Stäng</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
